import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { localize } from '../../i18n/script';

import Button from './Button';
import CalendarSessionSelector from './CalendarSessionSelector';
import Card from './Card';

interface Session {
  date: string;
  startTime: string;
  endTime: string;
  // Raw ISO end timestamp from the backend — forwarded to CalendarSessionSelector
  // so it can flag sessions that have already ended.
  endTimestamp?: string;
  maxCapacity: number;
  currentBookings?: number;
  price?: number | null; // cents, null = use activity default
  adultPrice?: number | null; // cents, null = use activity default
  description?: string | null;
}

interface ActivityPass {
  id: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  price: number; // cents
  totalSessions: number;
}

interface PriceCardProps {
  price: number;
  adultPrice?: number;
  childRequired?: boolean;
  ageMin?: number;
  sessions: Session[];
  onBook: (sessionIndex: number) => void;
  hasVariablePricing?: boolean;
  activityPriceCents?: number; // Activity default child price in cents (for session fallback)
  activityAdultPriceCents?: number; // Activity default adult price in cents (for session fallback)
  passes?: ActivityPass[];
  /** Optional: invoked when the selected session is full and the user wants
   *  to join the waitlist instead. PriceCard handles the button toggle. */
  onJoinWaitlist?: (sessionIndex: number) => void;
  /** Optional: pre-select a specific session by index (e.g. when arriving
   *  from a waitlist claim email link with ?sessionId=). */
  initialSelectedSession?: number;
}

type TabType = 'single' | 'pass';

export default function PriceCard({
  price,
  adultPrice,
  childRequired,
  sessions,
  onBook,
  hasVariablePricing,
  activityPriceCents = 0,
  activityAdultPriceCents = 0,
  passes = [],
  onJoinWaitlist,
  initialSelectedSession,
}: PriceCardProps) {
  const [selectedSession, setSelectedSession] = useState<number | undefined>(
    initialSelectedSession
  );
  const [activeTab, setActiveTab] = useState<TabType>('single');
  const { t, i18n } = useTranslation(['common', 'pass']);
  const navigate = useNavigate();
  const hasPasses = passes.length > 0;

  const selectedSessionData = selectedSession !== undefined ? sessions[selectedSession] : undefined;
  const selectedIsFull =
    !!selectedSessionData &&
    selectedSessionData.currentBookings !== undefined &&
    selectedSessionData.currentBookings >= selectedSessionData.maxCapacity;
  const showWaitlistCta = selectedIsFull && !!onJoinWaitlist;

  const handleBook = () => {
    if (selectedSession === undefined) return;
    if (showWaitlistCta && onJoinWaitlist) {
      onJoinWaitlist(selectedSession);
    } else {
      onBook(selectedSession);
    }
  };

  return (
    <Card className="sticky top-24" padding="lg">
      {/* Price - hidden when sessions have variable pricing */}
      {!hasVariablePricing && (
        <>
          <div className="mb-6">
            {(() => {
              const isAdultOnly = childRequired === false;
              if (isAdultOnly) {
                return (
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">${adultPrice ?? 0}</span>
                    <span className="text-gray-600">{t('common:pricing.perAdult')}</span>
                  </div>
                );
              }
              if (adultPrice != null && adultPrice > 0) {
                return (
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">${price}</span>
                      <span className="text-sm text-gray-600">{t('common:pricing.perChild')}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">${adultPrice}</span>
                      <span className="text-sm text-gray-600">{t('common:pricing.perAdult')}</span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">${price}</span>
                  <span className="text-gray-600">{t('common:pricing.perChild')}</span>
                </div>
              );
            })()}
          </div>

          {/* Divider */}
          <div className="mb-6" />
        </>
      )}

      {/* Tab Switcher - only shown when passes exist */}
      {hasPasses && (
        <div className="mb-5">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('single')}
              className={`flex-1 text-sm font-medium py-2 px-3 rounded-md transition-all ${
                activeTab === 'single'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('pass:activityDetail.tabSingleBooking')}
            </button>
            <button
              onClick={() => setActiveTab('pass')}
              className={`flex-1 text-sm font-medium py-2 px-3 rounded-md transition-all ${
                activeTab === 'pass'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('pass:activityDetail.tabClassPass')}
            </button>
          </div>
        </div>
      )}

      {/* Single Booking Tab Content (default, or always shown when no passes) */}
      {(activeTab === 'single' || !hasPasses) && (
        <>
          {/* Session Selector */}
          <div className="mb-6">
            <CalendarSessionSelector
              sessions={sessions}
              selectedSession={selectedSession}
              onSelectSession={setSelectedSession}
              activityPrice={activityPriceCents}
              activityAdultPrice={activityAdultPriceCents}
              childRequired={childRequired}
            />
          </div>

          {/* Book Now / Join Waitlist Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleBook}
            disabled={selectedSession === undefined}
            className="mb-3"
          >
            {showWaitlistCta ? t('waitlist:join.cta') : t('common:common.bookNow')}
          </Button>
        </>
      )}

      {/* Class Pass Tab Content */}
      {activeTab === 'pass' && hasPasses && (
        <div className="space-y-3">
          {passes.map((pass) => {
            const passTitle = localize(pass.titleCN, pass.titleEN, i18n.language);
            const perSessionPrice = (pass.price / pass.totalSessions / 100).toFixed(2);
            const isAdultOnly = childRequired === false;
            const basePriceCents = isAdultOnly ? activityAdultPriceCents : activityPriceCents;
            const singlePrice = basePriceCents / 100;
            const savingsPerSession = singlePrice - pass.price / pass.totalSessions / 100;
            const totalSavings = (savingsPerSession * pass.totalSessions).toFixed(0);

            return (
              <button
                key={pass.id}
                onClick={() => navigate(`/passes/${pass.slug}`)}
                className="w-full text-left bg-primary-50 border border-primary-200 rounded-xl px-4 py-3.5 hover:bg-primary-100 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-sm truncate">{passTitle}</p>
                      {singlePrice > 0 && savingsPerSession > 0 && (
                        <span className="inline-flex items-center bg-green-100 text-green-700 text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0">
                          {t('pass:activityDetail.saveBadge', { amount: totalSavings })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('pass:activityDetail.sessionCount', { count: pass.totalSessions })}
                      {' · '}
                      {t('pass:activityDetail.perSession', { price: perSessionPrice })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3 shrink-0">
                    <span className="font-bold text-primary text-base">
                      ${(pass.price / 100).toFixed(2)}
                    </span>
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
